import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import TenantList from './components/TenantList';
import { WmsTenant } from './types/WmsTenant';
import { Header } from './components/common/Header';
import { Card } from './components/common/Card';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginForm from './components/auth/LoginForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import UserManagement from './components/admin/UserManagement';
import { LogViewer } from './components/logs/LogViewer';
import TriggerCompliance from './components/triggers/TriggerCompliance';
import SettingDefinitionManagement from './components/settings/SettingDefinitionManagement';
import { MigrationDashboard } from './components/migrations/MigrationDashboard';
import { CleanupTablesSection } from './components/tenant/CleanupTablesSection';
import ReleaseNotes from './components/releasenotes/ReleaseNotes';
import DataPurgeManagement from './components/datapurge/DataPurgeManagement';
import DeveloperUserManagement from './components/devusers/DeveloperUserManagement';
import ActivityDashboard from './components/dashboard/ActivityDashboard';
import { fetchDashboardKey } from './services/activityDashboardApi';
import { Monitor } from 'lucide-react';

type ActiveTab = 'tenants' | 'users' | 'devusers' | 'logs' | 'triggers' | 'migrations' | 'settings' | 'releasenotes' | 'datapurge';
type TenantSubTab = 'list' | 'cleanup-tables';

const MainApp: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('tenants');
  const [tenantSubTab, setTenantSubTab] = useState<TenantSubTab>('list');
  const { user, logout } = useAuth();

  const handleEditTenant = (tenant: WmsTenant) => {
    // This is now handled inline in TenantList
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        title="Compass WMS"
        subtitle="Master Database Administration"
      >
        <div className="flex items-center space-x-4">
          <button
            onClick={async () => {
              try {
                const key = await fetchDashboardKey();
                window.open(`/storskjerm?key=${key}`, '_blank');
              } catch {
                alert('Kunne ikke hente storskjerm-lenken');
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            title="Åpne aktivitetsdashbord for storskjerm i ny fane"
          >
            <Monitor className="w-4 h-4" />
            Storskjerm
          </button>
          <span className="text-sm text-gray-600">Velkommen, {user?.username}</span>
          <button
            onClick={logout}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Logg ut
          </button>
        </div>
      </Header>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('tenants')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tenants'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tenanter
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Brukere
          </button>
          <button
            onClick={() => setActiveTab('devusers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'devusers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Utviklerbrukere
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Logger
          </button>
          <button
            onClick={() => setActiveTab('triggers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'triggers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Triggere
          </button>
          <button
            onClick={() => setActiveTab('migrations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'migrations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Databaseoppdateringer
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Egenskaper for Profil/System
          </button>
          <button
            onClick={() => setActiveTab('releasenotes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'releasenotes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Release Notes
          </button>
          <button
            onClick={() => setActiveTab('datapurge')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'datapurge'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Datapurge
          </button>
        </nav>
      </div>

      <div className="w-full px-6 py-8">
        <Card className="overflow-hidden w-full">
          <div className="p-6 w-full">
            {activeTab === 'tenants' ? (
              <div>
                {/* Sub-navigation under Tenanter */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="flex space-x-6" aria-label="Tenant sub-tabs">
                    <button
                      onClick={() => setTenantSubTab('list')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        tenantSubTab === 'list'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Tenant-liste
                    </button>
                    <button
                      onClick={() => setTenantSubTab('cleanup-tables')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        tenantSubTab === 'cleanup-tables'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Cleanup-tabeller
                    </button>
                  </nav>
                </div>

                {tenantSubTab === 'list' ? (
                  <TenantList onEditTenant={handleEditTenant} />
                ) : (
                  <CleanupTablesSection />
                )}
              </div>
            ) : activeTab === 'users' ? (
              <UserManagement />
            ) : activeTab === 'devusers' ? (
              <DeveloperUserManagement />
            ) : activeTab === 'logs' ? (
              <LogViewer />
            ) : activeTab === 'triggers' ? (
              <TriggerCompliance />
            ) : activeTab === 'migrations' ? (
              <MigrationDashboard />
            ) : activeTab === 'settings' ? (
              <SettingDefinitionManagement />
            ) : activeTab === 'releasenotes' ? (
              <ReleaseNotes />
            ) : (
              <DataPurgeManagement />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/reset-password" element={<ResetPasswordForm />} />
      {/* Storskjerm-dashbord: egen visnings-nøkkel i URL, krever ikke innlogging */}
      <Route path="/storskjerm" element={<ActivityDashboard />} />
      <Route 
        path="/" 
        element={
          isAuthenticated ? <MainApp /> : <LoginForm />
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
